import { Typography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { formatTimeAgo } from "@/zapi-frontend/utils/dates";
import { TNotification } from "../model/store";
import { CheckCircle, ChatCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";

type TNotificationItemProps = {
  notification: TNotification;
  onMarkAsRead: (id: number) => void;
  onClose?: () => void;
};

export function NotificationItem({ notification, onMarkAsRead, onClose }: TNotificationItemProps) {
  const navigate = useNavigate();

  const getNotificationContent = () => {
    const chapterName = notification.projectChapter.projectTemplateChapter?.name;

    switch (notification.type) {
      case "chapter_ready":
        return {
          title: `Chapter Ready: ${chapterName || "Unknown Chapter"}`,
          subtitle: "",
          icon: CheckCircle
        };
      case "chat_responded":
        return {
          title: `Chat responded: ${chapterName || "Unknown Chapter"}`,
          subtitle: "",
          icon: ChatCircle
        };
      default:
        return {
          title: "New notification",
          subtitle: "You have a new notification.",
          icon: CheckCircle
        };
    }
  };

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    const projectId = notification.projectChapter.projectId.toString();

    navigate(`${getRouteProjectDetails(projectId)}?tab=document&chapterId=${notification.projectChapterId}`, {
      replace: false,
      state: { chapterId: notification.projectChapterId }
    });
    onClose?.();
  };

  const content = getNotificationContent();
  const Icon = content.icon;

  return (
    <div
      className={cn(
        "pl-6 pr-3 py-2 ml-3 transition-colors cursor-pointer hover:bg-innoveraZinc-100/50 border-l-2",
        notification.isRead ? "border-transparent" : "border-innoveraOrange-400"
      )}
      onClick={handleClick}
    >
      <div className="flex gap-2 items-center">
        <div className="flex-shrink-0">
          <Icon
            size={16}
            weight={notification.isRead ? "regular" : "fill"}
            className={notification.isRead ? "text-innoveraZinc-400" : "text-innoveraOrange-400"}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <Typography
              variant="body2"
              className={cn("leading-tight", notification.isRead ? "text-innoveraZinc-600" : "text-innoveraZinc-900")}
            >
              {content.title}
            </Typography>
            <Typography variant="body2" className="text-innoveraZinc-400 text-xs whitespace-nowrap flex-shrink-0">
              {formatTimeAgo(notification.createdAt)}
            </Typography>
          </div>
          {content.subtitle && (
            <Typography variant="body2" className="text-innoveraZinc-500 leading-snug mt-0.5 text-sm">
              {content.subtitle}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}
